import React from 'react';
import { Star, ArrowRight } from 'lucide-react';
import { trackLead, trackEvent } from '../utils/metaPixel';

const MetaLandingPage: React.FC = () => {
  const handleTelegramClick = () => {
    // Track Meta Pixel events
    trackLead();
    trackEvent('TelegramJoin', { 
      source: 'meta_ad_landing_page',
      cta_type: 'primary_button'
    });
    
    // Replace with your actual Telegram channel/group link
    window.open('https://tinyurl.com/stockmarketgpt', '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Hero Section - Matching your ad design */}
      <div className="max-w-5xl mx-auto px-4 py-12 text-center">
        {/* Main Headline - Bold yellow/orange like your ad */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 leading-tight">
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
            RESEARCH SMARTER.
          </span>
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
            DECIDE BETTER.
          </span>
        </h1>

        {/* Subheading - Clean and professional like your ad */}
        <p className="text-lg md:text-xl text-slate-300 mb-8 font-medium max-w-3xl mx-auto">
          Free AI-powered market education for Indian retail investors. Understand any NSE stock in under 90 seconds.
        </p>

        {/* AI Assistant Call-out - Improved styling */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-600 mb-8 max-w-xl mx-auto">
          <div className="mb-4">
            <p className="text-slate-400 text-xs uppercase tracking-wider mb-2 font-medium">
              Use Your AI Assistant At
            </p>
            <div className="text-xl md:text-2xl font-bold bg-gradient-to-r from-blue-400 to-green-400 bg-clip-text text-transparent mb-4">
              www.IndianStockMarketGPT.com
            </div>
          </div>
          
          {/* Primary CTA Button - Matching your website style */}
          <button
            onClick={() => {
              // Data-layer push for GTM
              try { (window as any).dataLayer = (window as any).dataLayer || []; (window as any).dataLayer.push({ event: 'ad_lead_cta', channel: 'meta', campaign: 'meta-landing' }); } catch { /* tracking is best-effort */ }
              handleTelegramClick();
            }}
            data-event="ad_lead_cta"
            data-channel="meta"
            data-campaign="meta-landing"
            className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white font-bold py-3 px-6 rounded-xl text-base transition-all duration-300 transform hover:scale-105 shadow-lg mb-3 inline-flex items-center gap-2"
          >
            <span className="text-lg">🚀</span>
            Join Free AI Market Education
          </button>

          <p className="text-slate-400 text-xs">
            ✅ 25,000+ investors · Free educational research · No investment advice
          </p>
        </div>

        {/* Social Proof */}
        <div className="bg-slate-700/50 p-6 rounded-2xl border border-slate-600 mb-8 max-w-3xl mx-auto">
          <div className="flex items-center justify-center mb-3">
            <div className="flex text-yellow-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-current" />
              ))}
            </div>
          </div>
          <p className="text-slate-300 text-base italic mb-3 leading-relaxed">
            "The AI research summaries save me hours every week. Instead of reading 5 different sources, I get a clear plain-English breakdown of any stock in under 2 minutes."
          </p>
          <p className="text-slate-400 text-sm">- Dr. Ruchi Ughade · Retail Investor</p>
        </div>

        {/* Secondary CTA */}
        <div className="text-center">
          <p className="text-slate-400 mb-3 text-base">Want to try it first?</p>
          <a 
            href="https://chatgpt.com/g/g-jkxrUFNja-indian-stock-market"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-blue-400 hover:text-blue-300 font-semibold text-base transition-colors"
            onClick={() => trackEvent('SecondaryClick', { 
              source: 'meta_ad_landing_page',
              cta_type: 'secondary_link',
              destination: 'chatgpt_demo'
            })}
          >
            Try Our Free AI Assistant
            <ArrowRight className="w-4 h-4 ml-2" />
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-slate-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="bg-gradient-to-r from-blue-500 to-green-500 p-2 rounded-lg">
                  {/* Logo icon can go here if needed */}
                </div>
                <span className="text-xl font-bold">Indian Stock Market GPT</span>
              </div>
              <p className="text-slate-400 mb-4 max-w-md">
                AI-powered stock market education for smarter research in Indian markets.
              </p>
              <p className="text-sm text-slate-500">
                © {new Date().getFullYear()} Indian Stock Market GPT. All rights reserved.
              </p>
              <p className="text-xs text-slate-600 mt-3 max-w-md">
                ⚠ Not SEBI registered. All content is for educational purposes only and does not constitute investment advice. Community opinion only. Past performance is not indicative of future returns.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-slate-400">
                <li>
                  <a 
                    href="https://indianstockmarketgpt.com/features" 
                    className="hover:text-white transition-colors text-left"
                    onClick={() => trackEvent('FooterClick', { 
                      source: 'meta_ad_landing_page',
                      link_type: 'features'
                    })}
                  >
                    Features
                  </a>
                </li>
                <li>
                  <a 
                    href="https://indianstockmarketgpt.com/blog" 
                    className="hover:text-white transition-colors text-left"
                    onClick={() => trackEvent('FooterClick', { 
                      source: 'meta_ad_landing_page',
                      link_type: 'blog'
                    })}
                  >
                    Blog
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-slate-400">
                <li>
                  <a 
                    href="https://indianstockmarketgpt.com/about" 
                    className="hover:text-white transition-colors text-left"
                    onClick={() => trackEvent('FooterClick', { 
                      source: 'meta_ad_landing_page',
                      link_type: 'about'
                    })}
                  >
                    About Us
                  </a>
                </li>
                <li>
                  <a 
                    href="https://indianstockmarketgpt.com/terms" 
                    className="hover:text-white transition-colors text-left"
                    onClick={() => trackEvent('FooterClick', { 
                      source: 'meta_ad_landing_page',
                      link_type: 'terms'
                    })}
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a 
                    href="https://indianstockmarketgpt.com/privacy" 
                    className="hover:text-white transition-colors text-left"
                    onClick={() => trackEvent('FooterClick', { 
                      source: 'meta_ad_landing_page',
                      link_type: 'privacy'
                    })}
                  >
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MetaLandingPage;
